import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { createPageUrl } from './utils'
import { logout } from '@/lib/auth-client'
import { useAuth } from '@/components/AuthContext'
import {
  LayoutDashboard,
  Users,
  Radio,
  FolderTree,
  FileText,
  GraduationCap,
  ScrollText,
  MessageSquareText,
  Mail,
  ShieldCheck,
  CreditCard,
  Heart,
  FileVideo,
  Images,
  FileBadge,
  Bell,
  BellRing,
  LogOut,
  Menu,
  X,
} from 'lucide-react'

export default function Layout({
  children,
  currentPageName,
}: {
  children: React.ReactNode
  currentPageName: string
}) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { user } = useAuth()
  const isSuperAdmin = user?.role === 'super_admin'

  useEffect(() => {
    if (!sidebarOpen) return
    if (window.matchMedia('(min-width: 1024px)').matches) return

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [sidebarOpen])

  const commonNavItems = [
    { name: 'Dashboard', icon: LayoutDashboard, path: 'Dashboard' },
    { name: 'Home Carousel', icon: Images, path: 'HomeCarouselManagement' },
    { name: 'Users', icon: Users, path: 'Users' },
    { name: 'Live Sessions', icon: Radio, path: 'LiveSessions' },
    { name: 'Courses', icon: GraduationCap, path: 'Courses' },
    {
      name: 'Certificates',
      icon: ScrollText,
      path: 'Certificates',
    },
    { name: 'Memberships', icon: CreditCard, path: 'MembershipManagement' },
    //{ name: 'Zoom Settings', icon: Settings2, path: 'ZoomSettings' },
    {
      name: 'Payment Transactions',
      icon: CreditCard,
      path: 'PaymentTransactions',
    },
    {
      name: 'Invoices & Billing',
      icon: CreditCard,
      path: 'InvoicesBillingExplorer',
    },
    {
      name: 'Sadhana',
      icon: FileVideo,
      path: 'SadhanaManagement',
    },
    { name: 'Categories', icon: FolderTree, path: 'Categories' },
    { name: 'Content Library', icon: FileText, path: 'ContentLibrary' },
    { name: 'Campaigns', icon: Heart, path: 'Campaigns' },
    { name: 'Message Logs', icon: MessageSquareText, path: 'MessageLogs' },
    { name: 'WhatsApp Reminders', icon: Bell, path: 'WhatsappTemplates' },
    { name: 'Email Templates', icon: Mail, path: 'EmailTemplates' },
  ]

  const superAdminOnlyItems = [
    //{ name: 'Purchases', icon: CreditCard, path: 'Purchases' },
    {
      name: 'Push Broadcasts',
      icon: BellRing,
      path: 'PushNotifications',
    },
    { name: 'Donations', icon: Heart, path: 'Donations' },
    { name: 'Donation receipt settings', icon: FileBadge, path: 'ReceiptSettings' },
    { name: 'Admin Management', icon: ShieldCheck, path: 'AdminManagement' },
  ]
  const navItems = isSuperAdmin
    ? [...commonNavItems, ...superAdminOnlyItems]
    : commonNavItems

  return (
    <div className="flex min-h-screen bg-gray-50 overflow-hidden">
      {/* Sidebar */}
      <aside
        className={`
        fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200
        transform transition-transform duration-200 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}
      >
        <div className="h-full min-h-0 flex flex-col">
          {/* Logo */}
          <div className="h-16 flex items-center px-6 border-b border-gray-200">
            <h1 className="text-xl font-semibold text-gray-900">
              Welcome Admin
            </h1>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-4">
            {navItems.map(item => {
              const Icon = item.icon
              const isActive = currentPageName === item.path
              return (
                <Link
                  key={item.path}
                  to={createPageUrl(item.path)}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center gap-3 px-6 py-2.5 text-sm font-medium transition-colors
                    ${
                      isActive
                        ? 'text-blue-600 bg-blue-50 border-r-2 border-blue-600'
                        : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                    }
                  `}
                >
                  <Icon className="w-5 h-5" />
                  {item.name}
                </Link>
              )
            })}
          </nav>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {/* Top bar */}
        <header className="h-16 bg-white border-b border-gray-200 flex items-center px-4 lg:px-6">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="lg:hidden p-2 text-gray-600 hover:text-gray-900"
          >
            {sidebarOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
          <div className="flex-1 text-sm text-gray-600">
            {user?.name ? `${user.name} (${user.role})` : ''}
          </div>
          <button
            onClick={() => logout(window.location.href)}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
          >
            <LogOut className="h-4 w-4" />
            Logout
          </button>
        </header>

        {/* Page content */}
        <main className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
          {children}
        </main>
      </div>
    </div>
  )
}
